import { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "./Button";
import { Modal } from "./Modal";
import { TagEditorModal } from "./TagEditorModal";
import {
  EditorTitleInput,
  FormCheckbox,
  FormInput,
  FormSelect,
  FormRow,
  FormField,
  DayOfWeekCheckboxes,
  FormSection,
} from "./FormFields";
import { PlacesSearchInput } from "./PlacesSearchInput";
import { POIImportForm } from "./POIImportForm";
import { SegmentedControl } from "./SegmentedControl";
import {
  DisclosureSummary,
  EditorDisclosure,
  EditorSection,
} from "./EditorSection";
import { ReadableTextField } from "./ReadableTextField";
import { TagChipInput } from "./TagChipInput";
import { PoiSourcesEditor } from "./PoiSourcesEditor";
import { PoiCollectionsEditor } from "./PoiCollectionsEditor";
import { useUIStore } from "../store/uiStore";
import { useShallow } from "zustand/react/shallow";
import { useLibraryStore, isOwnPoi } from "../store/libraryStore";
import { useCollectionsStore } from "../store/collectionsStore";
import type {
  IdealTimeOfDay,
  PlaceResult,
  Tag,
  PoiSource,
  POIPlannerExport,
} from "@poi-planner/shared";
import type { importItemToCreateData } from "../services/importService";
import { api } from "../services/api";
import { useConnectivityStore } from "../store/connectivityStore";
import { DEFAULT_PHOSPHOR_ICON, toPhosphorTagIcon } from "../utils/tagIcons";
import { basemapPlaceMapsUrl } from "../utils/poiMaps";

type EditorMode = "manual" | "import";

const ACCOMMODATION_TAG_NAMES = ["accommodation"];
const ACCOMMODATION_HELP =
  "Can be picked as a place to sleep; colour and check-in times are set per trip.";
const PRIVATE_HELP =
  "Hidden from people you share trips with, unless it's pinned or scheduled on a shared trip.";
const DAY_ABBREVIATIONS: Record<string, string> = {
  monday: "Mo",
  tuesday: "Tu",
  wednesday: "We",
  thursday: "Th",
  friday: "Fr",
  saturday: "Sa",
  sunday: "Su",
};

// Type for tracking original form values (dirty state detection)
interface OriginalFormValues {
  name: string;
  lat: string;
  lng: string;
  timeBudget: string;
  tags: string[];
  primaryTagId: string | null;
  hasAvailability: boolean;
  availStart: string;
  availEnd: string;
  hasIdealTiming: boolean;
  idealDays: string[];
  idealTimeOfDay: string;
  hasWeather: boolean;
  weatherCondition: string;
  weatherSensitivity: string;
  notes: string;
  googleMapsUrl: string;
  sources: PoiSource[];
  isPrivate: boolean;
}

const TIME_OF_DAY_OPTIONS = [
  { value: "", label: "-- None --" },
  { value: "morning", label: "Morning" },
  { value: "afternoon", label: "Afternoon" },
  { value: "evening", label: "Evening" },
];

const SENSITIVITY_OPTIONS = [
  { value: "desired", label: "Desired" },
  { value: "required", label: "Required" },
];

export function POIEditorModal() {
  const { activeEditor, closeEditor, poiDraftSeed } = useUIStore(
    useShallow((s) => ({
      activeEditor: s.activeEditor,
      closeEditor: s.closeEditor,
      poiDraftSeed: s.poiDraftSeed,
    })),
  );
  const poiEditorOpen = activeEditor?.type === "poi";
  const poiEditorMode = poiEditorOpen ? activeEditor.mode : "create";
  const poiEditorId = poiEditorOpen ? activeEditor.entityId : null;
  const {
    getPOI,
    createPOI,
    updatePOI,
    deletePOI,
    createTag,
    updateTag,
    tags: allTags,
    pois: allPois,
    loadLibrary,
  } = useLibraryStore();
  const collections = useCollectionsStore((state) => state.collections);

  // Editor mode: manual entry or JSON import
  const [editorMode, setEditorMode] = useState<EditorMode>("manual");

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Original values for dirty state tracking (edit mode only)
  const originalValuesRef = useRef<OriginalFormValues | null>(null);

  // Collapsible sections open when they have content, decided on open.
  const [coordsOpen, setCoordsOpen] = useState(false);
  const [constraintsOpen, setConstraintsOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const [collectionPickerOpen, setCollectionPickerOpen] = useState(false);

  // Get all unique source texts for autocomplete
  const allSourceTexts = useMemo(() => {
    const sourceSet = new Set<string>();
    for (const poi of allPois) {
      for (const source of poi.sources ?? []) {
        if (source.text.trim()) {
          sourceSet.add(source.text.trim());
        }
      }
    }
    return Array.from(sourceSet).sort();
  }, [allPois]);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);

  const [name, setName] = useState("");
  const [primaryTagId, setPrimaryTagId] = useState<string | null>(null);
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [timeBudget, setTimeBudget] = useState("60");
  const [tags, setTags] = useState<string[]>([]);
  const [hasAvailability, setHasAvailability] = useState(false);
  const [availStart, setAvailStart] = useState("");
  const [availEnd, setAvailEnd] = useState("");
  const [idealDays, setIdealDays] = useState<string[]>([]);
  const [idealTimeOfDay, setIdealTimeOfDay] = useState("");
  const [hasIdealTiming, setHasIdealTiming] = useState(false);
  const [hasWeather, setHasWeather] = useState(false);
  const [weatherCondition, setWeatherCondition] = useState("");
  const [weatherSensitivity, setWeatherSensitivity] = useState("desired");
  const [notes, setNotes] = useState("");
  const [googleMapsUrl, setGoogleMapsUrl] = useState("");
  const [previousName, setPreviousName] = useState<string | null>(null);
  const [sources, setSources] = useState<PoiSource[]>([]);
  const [isPrivate, setIsPrivate] = useState(false);

  const editingPoi = poiEditorId ? getPOI(poiEditorId) : undefined;
  const offline = useConnectivityStore((s) => s.mode === "offline");
  const canEditPoi = !offline && (!editingPoi || isOwnPoi(editingPoi));
  const accommodationTag = allTags.find(
    (tag) => tag.name.toLowerCase() === "accommodation",
  );
  const hasAccommodationTag = Boolean(
    accommodationTag && tags.includes(accommodationTag.id),
  );

  // Reset form when modal opens/changes
  useEffect(() => {
    if (!poiEditorOpen) return;

    // Reset to manual mode on open
    setEditorMode("manual");
    setShowDeleteConfirm(false);

    if (poiEditorMode === "edit" && poiEditorId) {
      const poi = getPOI(poiEditorId);
      if (poi) {
        const latStr = poi.lat != null ? String(poi.lat) : "";
        const lngStr = poi.lng != null ? String(poi.lng) : "";
        const tagsArr = [...(poi.tags ?? [])];
        const hasAvail = !!poi.availability;
        const availStartStr = poi.availability?.start ?? "";
        const availEndStr = poi.availability?.end ?? "";
        const idealDaysArr = poi.idealDays ?? [];
        const idealTimeStr = poi.idealTimeOfDay ?? "";
        const hasIdeal =
          (poi.idealDays && poi.idealDays.length > 0) || !!poi.idealTimeOfDay;
        const hasWeatherReq = !!poi.weatherRequirement;
        const weatherCond = poi.weatherRequirement?.condition ?? "";
        const weatherSens = poi.weatherRequirement?.sensitivity ?? "desired";
        const notesStr = poi.notes ?? "";
        const mapsUrl = poi.googleMapsUrl ?? "";
        const sourcesArr = poi.sources ?? [];
        const isPrivateVal = poi.private ?? false;

        setName(poi.name);
        setLat(latStr);
        setLng(lngStr);
        setTimeBudget(String(poi.timeBudget));
        setTags(tagsArr);
        setHasAvailability(hasAvail);
        setAvailStart(availStartStr);
        setAvailEnd(availEndStr);
        setIdealDays(idealDaysArr);
        setIdealTimeOfDay(idealTimeStr);
        setHasIdealTiming(hasIdeal);
        setHasWeather(hasWeatherReq);
        setWeatherCondition(weatherCond);
        setWeatherSensitivity(weatherSens);
        setNotes(notesStr);
        setGoogleMapsUrl(mapsUrl);
        setPrimaryTagId(poi.primaryTagId ?? null);
        setPreviousName(null);
        setSources(sourcesArr);
        setIsPrivate(isPrivateVal);

        setCoordsOpen(!latStr || !lngStr);
        setConstraintsOpen(hasAvail || !!hasIdeal || hasWeatherReq);
        setSourcesOpen(sourcesArr.length > 0);
        setCollectionsOpen(
          useCollectionsStore.getState().getCollectionsForPoi(poi.id).length >
            0,
        );
        setCollectionPickerOpen(false);

        // Store original values for dirty state tracking
        originalValuesRef.current = {
          name: poi.name,
          lat: latStr,
          lng: lngStr,
          timeBudget: String(poi.timeBudget),
          tags: tagsArr,
          primaryTagId: poi.primaryTagId ?? null,
          hasAvailability: hasAvail,
          availStart: availStartStr,
          availEnd: availEndStr,
          hasIdealTiming: hasIdeal,
          idealDays: idealDaysArr,
          idealTimeOfDay: idealTimeStr,
          hasWeather: hasWeatherReq,
          weatherCondition: weatherCond,
          weatherSensitivity: weatherSens,
          notes: notesStr,
          googleMapsUrl: mapsUrl,
          sources: sourcesArr,
          isPrivate: isPrivateVal,
        };
        return;
      }
    }
    // Create mode defaults, optionally prefilled by whatever opened the editor
    // (for example a basemap point of interest clicked on the map).
    setName(poiDraftSeed?.name ?? "");
    setLat(poiDraftSeed ? String(poiDraftSeed.lat) : "");
    setLng(poiDraftSeed ? String(poiDraftSeed.lng) : "");
    setTimeBudget("60");
    setTags([]);
    setPrimaryTagId(null);
    setHasAvailability(false);
    setAvailStart("");
    setAvailEnd("");
    setIdealDays([]);
    setIdealTimeOfDay("");
    setHasIdealTiming(false);
    setHasWeather(false);
    setWeatherCondition("");
    setWeatherSensitivity("desired");
    setNotes("");
    setGoogleMapsUrl(poiDraftSeed?.googleMapsUrl ?? "");
    setPreviousName(null);
    setSources([]);
    setIsPrivate(false);
    setCoordsOpen(!poiDraftSeed);
    setConstraintsOpen(false);
    setSourcesOpen(false);
    setCollectionsOpen(false);
    setCollectionPickerOpen(false);
    originalValuesRef.current = null; // No original values in create mode
  }, [poiEditorOpen, poiEditorMode, poiEditorId, getPOI, poiDraftSeed]);

  // Compute whether form has been modified (dirty state)
  const isDirty = useMemo(() => {
    // In create mode, always allow saving (if name is valid)
    if (!originalValuesRef.current) return true;

    const orig = originalValuesRef.current;
    return (
      name !== orig.name ||
      lat !== orig.lat ||
      lng !== orig.lng ||
      timeBudget !== orig.timeBudget ||
      primaryTagId !== orig.primaryTagId ||
      hasAvailability !== orig.hasAvailability ||
      availStart !== orig.availStart ||
      availEnd !== orig.availEnd ||
      hasIdealTiming !== orig.hasIdealTiming ||
      idealTimeOfDay !== orig.idealTimeOfDay ||
      hasWeather !== orig.hasWeather ||
      weatherCondition !== orig.weatherCondition ||
      weatherSensitivity !== orig.weatherSensitivity ||
      notes !== orig.notes ||
      googleMapsUrl !== orig.googleMapsUrl ||
      isPrivate !== orig.isPrivate ||
      JSON.stringify([...tags].sort()) !==
        JSON.stringify([...orig.tags].sort()) ||
      JSON.stringify([...idealDays].sort()) !==
        JSON.stringify([...orig.idealDays].sort()) ||
      JSON.stringify(sources) !== JSON.stringify(orig.sources)
    );
  }, [
    name,
    lat,
    lng,
    timeBudget,
    tags,
    primaryTagId,
    hasAvailability,
    availStart,
    availEnd,
    hasIdealTiming,
    idealDays,
    idealTimeOfDay,
    hasWeather,
    weatherCondition,
    weatherSensitivity,
    notes,
    googleMapsUrl,
    sources,
    isPrivate,
  ]);

  const handlePlaceSelect = (place: PlaceResult) => {
    // Store current name for undo (only if user typed something)
    if (name.trim() && name !== place.name) {
      setPreviousName(name);
    }
    setName(place.name);
    setLat(String(place.lat));
    setLng(String(place.lng));
    setGoogleMapsUrl(place.googleMapsUrl);
  };

  const handleUndoName = () => {
    if (previousName !== null) {
      setName(previousName);
      setPreviousName(null);
    }
  };

  const handleSave = () => {
    if (!name.trim()) return;
    const parsedLat = parseFloat(lat);
    const parsedLng = parseFloat(lng);

    const data = {
      name: name.trim(),
      lat: !isNaN(parsedLat) ? parsedLat : null,
      lng: !isNaN(parsedLng) ? parsedLng : null,
      timeBudget: parseInt(timeBudget) || 60,
      tags,
      primaryTagId,
      availability:
        hasAvailability && availStart && availEnd
          ? { start: availStart, end: availEnd }
          : null,
      idealDays: hasIdealTiming && idealDays.length > 0 ? idealDays : null,
      idealTimeOfDay:
        hasIdealTiming && idealTimeOfDay
          ? (idealTimeOfDay as IdealTimeOfDay)
          : null,
      weatherRequirement:
        hasWeather && weatherCondition.trim()
          ? {
              condition: weatherCondition.trim(),
              sensitivity: weatherSensitivity as "required" | "desired",
            }
          : null,
      notes: notes.trim() || null,
      googleMapsUrl: googleMapsUrl || undefined,
      sources: sources.filter((s) => s.text.trim()), // Only save non-empty sources
      private: isPrivate,
    };

    if (poiEditorMode === "edit" && poiEditorId) {
      if (!canEditPoi) return;
      updatePOI(poiEditorId, data);
    } else {
      createPOI(data);
    }
    closeEditor();
  };

  const handleDelete = () => {
    if (poiEditorId && canEditPoi) {
      deletePOI(poiEditorId);
      closeEditor();
    }
  };

  // Handle import from JSON
  const handleImport = async (
    pois: ReturnType<typeof importItemToCreateData>[],
    takeout?: POIPlannerExport,
  ) => {
    if (takeout?.library) {
      await api.importLibrary(takeout.library);
      await loadLibrary();
      closeEditor();
      return;
    }
    const tagIdByName = new Map(
      allTags.map((tag) => [tag.name.toLowerCase(), tag.id]),
    );
    for (const poi of pois) {
      const tagIds: string[] = [];
      for (const tagName of poi.tags) {
        const normalized = tagName.trim().toLowerCase();
        let tagId = tagIdByName.get(normalized);
        if (!tagId) {
          tagId = await createTag({
            name: tagName.trim(),
            icon: toPhosphorTagIcon(DEFAULT_PHOSPHOR_ICON),
          });
          tagIdByName.set(normalized, tagId);
        }
        tagIds.push(tagId);
      }
      createPOI({ ...poi, tags: tagIds, primaryTagId: null });
    }
    closeEditor();
  };

  const isEdit = poiEditorMode === "edit";
  const showImportTab = !isEdit; // Only show import tab in create mode

  // Places-linked POIs keep their canonical URL; otherwise search the name
  // around the entered coordinates, like the map's basemap place popup.
  const parsedMapsLat = parseFloat(lat);
  const parsedMapsLng = parseFloat(lng);
  const viewInMapsUrl =
    googleMapsUrl.trim() ||
    (Number.isFinite(parsedMapsLat) && Number.isFinite(parsedMapsLng)
      ? basemapPlaceMapsUrl({
          name,
          lat: parsedMapsLat,
          lng: parsedMapsLng,
        })
      : null);

  const setAccommodation = async (checked: boolean) => {
    if (!checked) {
      if (accommodationTag) {
        setTags(tags.filter((tagId) => tagId !== accommodationTag.id));
      }
      return;
    }
    const tagId =
      accommodationTag?.id ??
      (await createTag({
        name: "accommodation",
        icon: toPhosphorTagIcon(DEFAULT_PHOSPHOR_ICON),
      }));
    setTags((current) => [...new Set([...current, tagId])]);
  };

  const constraintSummary = [
    hasAvailability &&
      (availStart && availEnd ? `${availStart} – ${availEnd}` : "Dates"),
    hasIdealTiming &&
      ([
        idealDays.map((day) => DAY_ABBREVIATIONS[day] ?? day).join(" "),
        TIME_OF_DAY_OPTIONS.find(
          (option) => option.value && option.value === idealTimeOfDay,
        )?.label,
      ]
        .filter(Boolean)
        .join(", ") ||
        "Best times"),
    hasWeather && (weatherCondition.trim() || "Weather"),
  ]
    .filter(Boolean)
    .join(" · ");

  const namedSources = sources
    .map((source) => source.text.trim())
    .filter(Boolean);

  const poiCollectionNames = poiEditorId
    ? collections
        .filter((collection) => collection.poiIds.includes(poiEditorId))
        .map((collection) => collection.name)
    : [];

  return (
    <Modal
      open={poiEditorOpen}
      onClose={closeEditor}
      title={isEdit ? (canEditPoi ? "Edit POI" : "View POI") : "Create POI"}
      icon="location_on"
      size="lg"
      footer={
        editorMode === "import" ? null : !canEditPoi ? (
          <Button onClick={closeEditor} variant="secondary">
            Close
          </Button>
        ) : (
          <>
            {isEdit && !showDeleteConfirm && (
              <Button
                onClick={() => setShowDeleteConfirm(true)}
                variant="danger"
                className="mr-auto"
              >
                Delete
              </Button>
            )}
            {isEdit && showDeleteConfirm && (
              <div className="flex items-center gap-2 mr-auto">
                <span className="text-sm text-secondary">Delete this POI?</span>
                <Button onClick={handleDelete} variant="danger">
                  Yes
                </Button>
                <Button
                  onClick={() => setShowDeleteConfirm(false)}
                  variant="secondary"
                >
                  No
                </Button>
              </div>
            )}
            <Button onClick={closeEditor} variant="secondary">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!name.trim() || (isEdit && !isDirty)}
              variant="primary"
            >
              {isEdit ? "Save" : "Create"}
            </Button>
          </>
        )
      }
    >
      {/* Mode toggle (only in create mode) */}
      {showImportTab && (
        <div className="mb-4">
          <SegmentedControl
            aria-label="How to add"
            fullWidth
            options={[
              { value: "manual", label: "Manual", icon: "edit" },
              { value: "import", label: "Import JSON", icon: "upload_file" },
            ]}
            value={editorMode}
            onChange={setEditorMode}
          />
        </div>
      )}

      {/* Import mode */}
      {editorMode === "import" && showImportTab && (
        <POIImportForm
          onImport={handleImport}
          onCancel={() => setEditorMode("manual")}
        />
      )}

      {/* Manual mode */}
      {editorMode === "manual" && (
        <fieldset
          disabled={!canEditPoi}
          className="m-0 flex min-w-0 flex-col gap-4 border-0 p-0"
        >
          {!canEditPoi && (
            <div className="flex items-center gap-2 rounded-lg border border-warning bg-warning-bg px-3 py-2 text-sm text-warning">
              <span className="material-symbols-outlined text-lg">lock</span>
              {offline
                ? "Offline — view only."
                : editingPoi?.ownerName
                  ? `Owned by ${editingPoi.ownerName} — view only.`
                  : "Owned by another user — view only."}
            </div>
          )}

          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <EditorTitleInput
                  value={name}
                  onChange={(v) => {
                    setName(v);
                    setPreviousName(null);
                  }}
                  placeholder="e.g., Central Park"
                />
              </div>
              {previousName !== null && (
                <Button
                  variant="icon"
                  icon="undo"
                  onClick={handleUndoName}
                  aria-label={`Restore name: ${previousName}`}
                  title={`Restore: ${previousName}`}
                />
              )}
            </div>
            <ReadableTextField
              id="poi-notes"
              label="Notes"
              value={notes}
              onChange={setNotes}
              placeholder="Opening hours, what to order, booking details…"
              disabled={!canEditPoi}
            />
          </div>

          <EditorSection
            id="poi-location-heading"
            title="Location"
            icon="pin_drop"
          >
            <div className="flex flex-col gap-2">
              <PlacesSearchInput
                onSelect={handlePlaceSelect}
                placeholder="Search Google Places to set the location"
                disabled={!canEditPoi}
              />
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-border px-3 py-2">
                <span className="flex flex-1 items-center gap-1.5 whitespace-nowrap text-sm tabular-nums text-secondary">
                  <span
                    className="material-symbols-outlined text-lg text-accent"
                    aria-hidden
                  >
                    location_on
                  </span>
                  {Number.isFinite(parsedMapsLat) &&
                  Number.isFinite(parsedMapsLng)
                    ? `${parsedMapsLat.toFixed(5)}, ${parsedMapsLng.toFixed(5)}`
                    : "No location yet"}
                </span>
                {viewInMapsUrl && (
                  <a
                    href={viewInMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 text-sm font-semibold text-info no-underline hover:underline"
                  >
                    Open in Maps
                    <span
                      className="material-symbols-outlined icon-match-text"
                      aria-hidden
                    >
                      arrow_outward
                    </span>
                  </a>
                )}
                <Button
                  variant="unstyled"
                  aria-expanded={coordsOpen}
                  aria-controls="poi-coordinates"
                  onClick={() => setCoordsOpen(!coordsOpen)}
                  className="cursor-pointer bg-transparent p-0 text-sm font-semibold text-info hover:underline"
                >
                  {coordsOpen ? "Hide coordinates" : "Edit coordinates"}
                </Button>
              </div>
              <div id="poi-coordinates" hidden={!coordsOpen}>
                {coordsOpen && (
                  <FormRow>
                    <FormField label="Latitude" flex={1}>
                      <FormInput
                        value={lat}
                        onChange={setLat}
                        type="number"
                        step="any"
                        placeholder="40.7829"
                      />
                    </FormField>
                    <FormField label="Longitude" flex={1}>
                      <FormInput
                        value={lng}
                        onChange={setLng}
                        type="number"
                        step="any"
                        placeholder="-73.9654"
                      />
                    </FormField>
                  </FormRow>
                )}
              </div>
            </div>
          </EditorSection>

          <div className="flex flex-col gap-1.5 border-t border-border pt-4">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
              <label
                htmlFor="poi-time-budget"
                className="inline-flex items-center gap-2 text-sm font-semibold text-secondary"
              >
                <span
                  className="material-symbols-outlined text-lg text-tertiary"
                  aria-hidden
                >
                  timer
                </span>
                Time to allow
                <span className="relative w-24">
                  <FormInput
                    id="poi-time-budget"
                    value={timeBudget}
                    onChange={setTimeBudget}
                    type="number"
                    min="0"
                    step="15"
                    placeholder="60"
                    className="py-1 pr-10 tabular-nums"
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-tertiary">
                    min
                  </span>
                </span>
              </label>
              <span className="flex flex-wrap gap-x-4 gap-y-1">
                <FormCheckbox
                  label="Accommodation"
                  icon="hotel"
                  checked={hasAccommodationTag}
                  disabled={!canEditPoi}
                  title={ACCOMMODATION_HELP}
                  onChange={(checked) => void setAccommodation(checked)}
                />
                <FormCheckbox
                  label="Private"
                  icon="visibility_off"
                  checked={isPrivate}
                  disabled={!canEditPoi}
                  title={PRIVATE_HELP}
                  onChange={setIsPrivate}
                />
              </span>
            </div>
            {(hasAccommodationTag || isPrivate) && (
              <p className="m-0 text-xs text-tertiary">
                {[
                  hasAccommodationTag && ACCOMMODATION_HELP,
                  isPrivate && PRIVATE_HELP,
                ]
                  .filter(Boolean)
                  .join(" ")}
              </p>
            )}
          </div>

          <EditorSection id="poi-tags-heading" title="Tags" icon="label">
            <TagChipInput
              inputId="poi-tag-input"
              tags={tags}
              allTags={allTags}
              hiddenTagNames={ACCOMMODATION_TAG_NAMES}
              disabled={!canEditPoi}
              primary={{
                primaryTagId,
                onPrimaryChange: setPrimaryTagId,
                onEditTag: setEditingTag,
              }}
              onChange={(newTags) => {
                setTags(newTags);
                // Clear primary tag if it was removed
                if (primaryTagId && !newTags.includes(primaryTagId)) {
                  setPrimaryTagId(null);
                }
              }}
              onCreateTag={(tagName) =>
                createTag({
                  name: tagName,
                  icon: toPhosphorTagIcon(DEFAULT_PHOSPHOR_ICON),
                })
              }
            />
          </EditorSection>

          <div>
            <EditorDisclosure
              id="poi-constraints-heading"
              title="Planning constraints"
              icon="tune"
              open={constraintsOpen}
              onOpenChange={setConstraintsOpen}
              summary={
                constraintSummary ? (
                  <DisclosureSummary text={constraintSummary} />
                ) : null
              }
              emptyLabel="Dates, best times, weather"
            >
              <div className="flex flex-col gap-4">
                <FormSection
                  label="Availability Window"
                  enabled={hasAvailability}
                  onToggle={setHasAvailability}
                >
                  <FormRow>
                    <FormField label="Start" flex={1}>
                      <FormInput
                        value={availStart}
                        onChange={setAvailStart}
                        type="date"
                      />
                    </FormField>
                    <FormField label="End" flex={1}>
                      <FormInput
                        value={availEnd}
                        onChange={setAvailEnd}
                        type="date"
                      />
                    </FormField>
                  </FormRow>
                </FormSection>

                <FormSection
                  label="Ideal Days & Time"
                  enabled={hasIdealTiming}
                  onToggle={setHasIdealTiming}
                >
                  <div className="flex flex-col gap-3">
                    <FormField label="Ideal Days">
                      <DayOfWeekCheckboxes
                        selected={idealDays}
                        onChange={setIdealDays}
                      />
                    </FormField>
                    <FormField label="Ideal Time of Day">
                      <FormSelect
                        value={idealTimeOfDay}
                        onChange={setIdealTimeOfDay}
                        options={TIME_OF_DAY_OPTIONS}
                      />
                    </FormField>
                  </div>
                </FormSection>

                <FormSection
                  label="Weather Requirement"
                  enabled={hasWeather}
                  onToggle={setHasWeather}
                >
                  <div className="flex flex-col gap-2.5">
                    <FormField label="Condition">
                      <FormInput
                        value={weatherCondition}
                        onChange={setWeatherCondition}
                        placeholder="e.g., clear, no rain"
                      />
                    </FormField>
                    <FormField label="Sensitivity">
                      <FormSelect
                        value={weatherSensitivity}
                        onChange={setWeatherSensitivity}
                        options={SENSITIVITY_OPTIONS}
                      />
                    </FormField>
                  </div>
                </FormSection>
              </div>
            </EditorDisclosure>

            <EditorDisclosure
              id="poi-sources-heading"
              title="Sources"
              icon="bookmark"
              open={sourcesOpen}
              onOpenChange={(open) => {
                setSourcesOpen(open);
                if (open && sources.length === 0 && canEditPoi) {
                  setSources([{ text: "", url: "" }]);
                }
              }}
              summary={
                namedSources.length ? (
                  <DisclosureSummary
                    text={namedSources.join(", ")}
                    count={namedSources.length}
                  />
                ) : null
              }
              emptyLabel="Add where you found this"
            >
              <PoiSourcesEditor
                sources={sources}
                allSources={allSourceTexts}
                onChange={setSources}
                disabled={!canEditPoi}
              />
            </EditorDisclosure>

            {/* Collections only exist for saved POIs */}
            {isEdit && poiEditorId && (
              <EditorDisclosure
                id="poi-collections-heading"
                title="Collections"
                icon="folder_special"
                open={collectionsOpen}
                onOpenChange={(open) => {
                  setCollectionsOpen(open);
                  if (open && poiCollectionNames.length === 0) {
                    setCollectionPickerOpen(true);
                  }
                }}
                summary={
                  poiCollectionNames.length ? (
                    <DisclosureSummary
                      text={poiCollectionNames.join(", ")}
                      count={poiCollectionNames.length}
                    />
                  ) : null
                }
                emptyLabel="Add to a collection"
              >
                <PoiCollectionsEditor
                  poiId={poiEditorId}
                  pickerOpen={collectionPickerOpen}
                  onPickerOpenChange={setCollectionPickerOpen}
                  disabled={!canEditPoi}
                />
              </EditorDisclosure>
            )}
          </div>
        </fieldset>
      )}

      {/* Tag Editor Modal */}
      <TagEditorModal
        open={!!editingTag}
        onClose={() => setEditingTag(null)}
        tag={editingTag ?? undefined}
        onSave={async (data) => {
          if (editingTag) {
            await updateTag(editingTag.id, data);
          }
        }}
      />
    </Modal>
  );
}
